import React, { useState, useEffect, useCallback } from 'react';
import { getHostelRequestsPortal } from '../../api';
import {
  FiClipboard, FiChevronDown, FiChevronUp,
  FiEdit2, FiTrash2, FiLogOut, FiCheckSquare, FiCoffee,
} from 'react-icons/fi';

const STATUS_META = {
  pending: { badge: 'badge-gold' },
  approved: { badge: 'badge-success' },
  rejected: { badge: 'badge-danger' },
};

const CATEGORY_META = {
  edit: { label: 'Resident Edit', icon: FiEdit2, color: '#60a5fa' },
  delete: { label: 'Resident Delete', icon: FiTrash2, color: '#f87171' },
  checkout: { label: 'Checkout', icon: FiLogOut, color: '#a78bfa' },
  attendance_edit: { label: 'Attendance Correction', icon: FiCheckSquare, color: '#f5bc1f' },
  meal_edit: { label: 'Meal Correction', icon: FiCoffee, color: '#34d399' },
};

function formatJson(data) {
  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    return Object.entries(parsed).map(([k, v]) => `${k}: ${v}`).join('\n');
  } catch { return String(data); }
}

export default function HostelRequestsPortal() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedData, setExpandedData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const res = await getHostelRequestsPortal(params);
      setRequests(res.data);
    } catch (e) { /* non-fatal */ }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title"><FiClipboard /> My Requests</h2>
          <p style={{ margin: '4px 0 0' }}>Track the status of every edit or correction you've submitted for admin approval</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', margin: '18px 0', flexWrap: 'wrap' }}>
        {['', 'pending', 'approved', 'rejected'].map(s => (
          <button
            key={s || 'all'}
            onClick={() => setStatusFilter(s)}
            className={`btn btn-small ${statusFilter === s ? 'btn-primary' : 'btn-secondary'}`}
          >
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
            {s === 'pending' && pendingCount > 0 && (
              <span style={{ marginLeft: '6px', background: '#dc2626', color: '#fff', borderRadius: '999px', padding: '1px 7px', fontSize: '11px' }}>
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {requests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              You haven't submitted any requests{statusFilter ? ` with status "${statusFilter}"` : ''}.
            </div>
          ) : requests.map(req => {
            const meta = CATEGORY_META[req.request_type] || { label: req.request_type, icon: FiClipboard, color: '#94a3b8' };
            const Icon = meta.icon;
            return (
              <div key={req.id} className="page-container" style={{ marginBottom: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: `${meta.color}22`, color: meta.color, padding: '3px 11px', borderRadius: '999px', fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase' }}>
                        <Icon size={12} /> {meta.label}
                      </span>
                      <span className={`badge ${STATUS_META[req.status]?.badge || 'badge-muted'}`}>{req.status}</span>
                    </div>
                    <h4 style={{ margin: '8px 0 4px', fontSize: '15px', color: '#fff' }}>{req.resident_name || 'Unknown Resident'}</h4>
                    <p style={{ margin: 0, fontSize: '13px' }}>
                      Submitted: {req.created_at ? new Date(req.created_at).toLocaleDateString('en-PK') : '—'}
                      {req.resolved_at && ` · Resolved: ${new Date(req.resolved_at).toLocaleDateString('en-PK')}`}
                    </p>
                  </div>
                </div>

                {req.reason && (
                  <div style={{ marginTop: '10px', padding: '8px 12px', background: 'rgba(255,255,255,0.04)', borderRadius: '6px', fontSize: '13px' }}>
                    <strong style={{ color: '#fff' }}>Your reason:</strong> {req.reason}
                  </div>
                )}

                {req.requested_data && (
                  <div style={{ marginTop: '8px' }}>
                    <button
                      onClick={() => setExpandedData(expandedData === req.id ? null : req.id)}
                      style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '12px', padding: '4px 0', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      {expandedData === req.id ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />} {expandedData === req.id ? 'Hide' : 'View'} Details
                    </button>
                    {expandedData === req.id && (
                      <pre style={{ background: 'rgba(0,0,0,0.3)', padding: '10px', borderRadius: '6px', fontSize: '12px', color: '#f5bc1f', marginTop: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {formatJson(req.requested_data)}
                      </pre>
                    )}
                  </div>
                )}

                {req.admin_notes && (
                  <div style={{ marginTop: '8px', padding: '8px 12px', background: 'rgba(255,255,255,0.06)', borderRadius: '6px', fontSize: '13px' }}>
                    <strong style={{ color: '#fff' }}>Admin Notes:</strong> {req.admin_notes}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
